/**
 * Reading the currently-selected message out of Outlook.
 *
 * Everything in here is untrusted input. Email content is *data*: it is used to
 * prefill fields for a human to review, and must never drive an action on its own.
 */

export type MailboxSender = {
    emailAddress: string
    displayName: string
}

export type MailboxItem = {
    /** Stable per-message id, used to key caches and detect item switches. */
    key: string
    sender: MailboxSender | null
    subject: string
    receivedAt: Date | null
    internetMessageId: string | null
}

function toSender(details: Office.EmailAddressDetails | undefined | null): MailboxSender | null {
    const emailAddress = details?.emailAddress?.trim()
    if (!emailAddress) return null
    return {
        emailAddress,
        displayName: details?.displayName?.trim() || emailAddress,
    }
}

/**
 * Works out who the "other party" on this message is.
 *
 * For received mail that's the sender. For a message the user sent themselves,
 * `from` is the user, so the first recipient is the useful party instead.
 */
function resolveCounterparty(item: Office.MessageRead): MailboxSender | null {
    const from = toSender(item.from) ?? toSender(item.sender)
    const ownEmail = Office.context.mailbox.userProfile?.emailAddress?.trim().toLowerCase()

    if (from && (!ownEmail || from.emailAddress.toLowerCase() !== ownEmail)) {
        return from
    }

    const firstRecipient = item.to?.map(toSender).find((entry): entry is MailboxSender => entry !== null)
    return firstRecipient ?? from
}

/** Reads the metadata of the currently-selected message. Returns null if there isn't one. */
export function readCurrentItem(): MailboxItem | null {
    const item = Office.context.mailbox.item as Office.MessageRead | undefined

    if (!item) return null

    return {
        // itemId is absent for unsaved/compose items; internetMessageId is a reasonable
        // fallback and both are stable enough to key a cache on.
        key: item.itemId ?? item.internetMessageId ?? 'current',
        sender: resolveCounterparty(item),
        subject: item.subject?.trim() ?? '',
        receivedAt: item.dateTimeCreated instanceof Date ? item.dateTimeCreated : null,
        internetMessageId: item.internetMessageId ?? null,
    }
}

/** Reads the plain-text body of the current message. */
export function readCurrentBody(): Promise<string> {
    return new Promise((resolve, reject) => {
        const item = Office.context.mailbox.item as Office.MessageRead | undefined

        if (!item?.body) {
            resolve('')
            return
        }

        item.body.getAsync(Office.CoercionType.Text, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(result.value ?? '')
            } else {
                reject(new Error(result.error?.message ?? 'Could not read the email body'))
            }
        })
    })
}

/**
 * Calls back whenever the user selects a different message, so the pane follows the
 * reading pane instead of showing details for an email that is no longer on screen.
 * Returns an unsubscribe function.
 */
export function onItemChanged(handler: () => void): () => void {
    const wrapped = () => handler()

    try {
        Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, wrapped)
    } catch {
        // Requires Mailbox 1.5. On older hosts the pane simply won't auto-refresh.
        return () => {}
    }

    return () => {
        try {
            // Mailbox.removeHandlerAsync cannot target an individual handler — it clears
            // every handler for the event type. That's fine here because the pane
            // registers exactly one, but it's why `wrapped` isn't passed back in.
            Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged)
        } catch {
            // Best effort — the pane is being torn down anyway.
        }
    }
}
